import { Injectable } from '@angular/core';
import { Observable, defer, forkJoin, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, retryWhen } from 'rxjs/operators';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { WindowPayloadBuilderService } from './window-payload-builder.service';
import { environment } from 'src/environments/environment';

export interface ThermalResult {
  value: number;
  unit:  string;
}

export interface ThermalBothResult {
  uw:  ThermalResult;
  psi: ThermalResult;
}

@Injectable({ providedIn: 'root' })
export class ThermalApiService {

  private readonly RETRY_COUNT   = 2;
  private readonly BASE_DELAY_MS = 200;

  private readonly uwUrl  = environment.caluwin_api.base_url + '/api/Thermal/uw';
  private readonly psiUrl = environment.caluwin_api.base_url + '/api/Thermal/psi';

  constructor(private payloadBuilder: WindowPayloadBuilderService) {}

  // ─────────────────────────────────────────────
  // BOTH — call uw + psi in parallel, window1 only
  // ─────────────────────────────────────────────
  calculateBoth(): Observable<ThermalBothResult> {
    return defer(async () => {
      const { window1 } = await this.payloadBuilder.buildWindowPayloads();
      return window1;
    }).pipe(
      mergeMap(window1 =>
        forkJoin({
          uw:  this.postThermal(this.uwUrl,  window1),
          psi: this.postThermal(this.psiUrl, window1)
        })
      ),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  calculateUw(customData?: any): Observable<ThermalResult> {
    return defer(async () => {
      const { window1 } = await this.payloadBuilder.buildWindowPayloads(customData);
      return CapacitorHttp.post({ url: this.uwUrl, headers: this.getHeaders(), data: window1 });
    }).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  calculatePsi(isComparisonMode: boolean = false): Observable<ThermalResult> {
    return defer(async () => {
      const { window1, window2 } = await this.payloadBuilder.buildWindowPayloads();
      const payload = isComparisonMode ? window2 : window1;
      return CapacitorHttp.post({ url: this.psiUrl, headers: this.getHeaders(), data: payload });
    }).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  // ─────────────────────────────────────────────
  // PRIVATE HTTP
  // ─────────────────────────────────────────────
  private postThermal(url: string, payload: any): Observable<ThermalResult> {
    return defer(() =>
      CapacitorHttp.post({ url, headers: this.getHeaders(), data: payload })
    ).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  private getHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  private handleSuccessResponse(response: HttpResponse): ThermalResult {
    if (response.status < 200 || response.status >= 300) throw response;
    return response.data as ThermalResult;
  }

  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;
          if (attempt > this.RETRY_COUNT || !this.isRetryableError(error?.status)) {
            return throwError(() => error);
          }
          return timer(Math.pow(2, attempt) * this.BASE_DELAY_MS);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') {
      console.error('Thermal API Error:', error.status, error.data);
      return new Error(error.data?.message || `HTTP Error ${error.status}`);
    }
    console.error('Network Error:', error);
    return new Error('Network error. Please check your connection.');
  }
}
